<template>
  <VModal :disable-backdrop-click="processing" :disable-close="processing" @close="closeModal">
    <template #default="modalProps">
      <div class="mx-auto bg-white rounded-lg text-left shadow-xl lg:max-w-lg sm:w-full">
        <div class="flex flex-col divide-y">
          <div class="flex-none p-4">
            <div class="flex justify-between items-center">
              <div class="flex-1">Add contacts to list</div>
              <div class="flex-none">
                <button
                  type="button"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="modalProps.close"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="inline-flex h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div>
            <BaseVeeForm :action="submit" @success="closeModal" class="flex-1 p-4">
              <template #default="formProps">
                <div v-if="!processing" class="space-y-8">
                  <ValidationProvider
                    v-if="contactList === null || contactList.id !== undefined"
                    name="contact list"
                    slim
                    vid="contact_list_id"
                    rules="required"
                  >
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel> Contact List </BaseFormLabel>
                        <VSelect
                          v-model="contactList"
                          :filterable="false"
                          :options="contactListOptions"
                          label="name"
                          @search="onSearchContactLists"
                        >
                          <template #list-footer>
                            <li class="px-2 pt-2 border-t">
                              <BaseButton
                                primary
                                small
                                class="w-full"
                                @click="onCreateNewContactList"
                              >
                                Create new list
                              </BaseButton>
                            </li>
                          </template>
                          <template #no-options>
                            <div class="p-4">No contact lists found.</div>
                          </template>
                        </VSelect>
                        <BaseFormInput type="hidden" :value="contactListId" />
                        <BaseFormInlineMessage v-if="errors.length" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                  <div v-else>
                    <BaseFormTextFieldWithValidator
                      v-model="contactList.name"
                      :validator="{
                        name: 'contact list name',
                        vid: 'contact_list_name',
                        rules: {
                          required: true,
                        },
                      }"
                      :field="{
                        label: 'Contact list name',
                        id: 'contact_list_name',
                      }"
                      :disabled="formProps.isPending"
                    />
                    <a
                      role="button"
                      href="#"
                      class="text-xs mt-2"
                      @click.prevent="contactList = null"
                    >
                      Select existing contact list
                    </a>
                  </div>
                  <div class="flex space-x-2">
                    <BaseButton primary :disabled="formProps.isPending"> Save </BaseButton>
                    <BaseButton
                      type="button"
                      ghost
                      :disabled="formProps.isPending"
                      @click="modalProps.close"
                    >
                      Cancel
                    </BaseButton>
                  </div>
                </div>
                <div v-else class="text-center">
                  <Loader />
                  <p v-if="meta" class="mt-4">
                    Proceccing {{ meta.to }} out of {{ meta.total }} total requests
                  </p>
                </div>
              </template>
            </BaseVeeForm>
          </div>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script>
import {
  accountContactListsClient,
  accountContactsClient,
  contactListSubscribersClient,
} from '@/api/clients';
import { debounce } from 'lodash-es';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: 'AddContactsToListDialog',
  props: {
    accountId: {
      type: String,
      required: true,
    },
    query: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      contactList: null,
      contactListOptions: [],
      search: '',
      meta: null,
      processing: false,
    };
  },
  computed: {
    contactListId() {
      return this.contactList?.id || null;
    },
  },
  created() {
    this.loadContactListOptions();
  },
  methods: {
    loadContactListOptions() {
      const params = {};
      if (this.search) {
        params.filter = { search: this.search };
      }

      return accountContactListsClient.list(this.accountId, params).then((response) => {
        this.contactListOptions = response.data;
        return Promise.resolve();
      });
    },
    onSearchContactLists(search, loading) {
      if (search !== this.search) {
        loading(true);
        this.search = search;
        this.searchContactListOptions(loading, this);
      }
    },
    searchContactListOptions: debounce(
      (loading, vm) =>
        vm.loadContactListOptions().finally(() => {
          loading(false);
        }),
      350
    ),
    onCreateNewContactList() {
      this.contactList = {
        name: '',
      };
    },
    async storeContactList() {
      return accountContactListsClient
        .store(this.accountId, { name: this.contactList.name })
        .then((response) => {
          this.contactList = response.data;
          return Promise.resolve();
        });
    },
    async subscribeContacts() {
      try {
        const contactsResponse = await accountContactsClient.list(this.accountId, {
          filter: {
            ...this.query.filter,
            contact_list_not_in: this.contactList.id,
          },
          page: {
            size: 500,
          },
        });

        this.meta = contactsResponse.meta;

        if (contactsResponse.data.length === 0) {
          return Promise.resolve();
        }

        await Promise.all([
          contactListSubscribersClient.store(this.contactList.id, {
            contact_ids: contactsResponse.data.map((contact) => contact.id),
          }),
          sleep(1000),
        ]);

        return await this.subscribeContacts();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async submit() {
      this.processing = true;

      try {
        if (typeof this.contactList?.id === 'undefined') {
          await this.storeContactList();
        }

        await this.subscribeContacts();

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.processing = false;
      }
    },
    closeModal() {
      this.$emit('close');
    },
  },
};
</script>
